import { useRef, useState, useEffect } from 'react';
import PlainTextExpandingInput from '../../../../PTS/PlainTextExpandingInput';
import TaulukkoFooter from '../../TaulukkoFooter';
import TableLoadingOverlay from './TableLoadingOverlay';

// Pure view — duplicated from the original PoranayteBlock.jsx table layout
// (TABLE_ROWS structure, TestiCell-style checkbox group, same styling
// constants). No API calls; everything comes from usePoranayteV2 as props.

// Same pattern as VetolujuusView.jsx/OhuthieView.jsx/etc.
function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return mobile;
}

const C  = { border: '1px solid #adb5bd', padding: '6px 8px', fontSize: '0.82rem', verticalAlign: 'middle' };
const CL = { ...C, background: '#f8f9fa', fontWeight: 600, whiteSpace: 'pre-line', width: 150 };
const CG = { ...C, background: '#04aa00', color: '#fff', fontWeight: 700, textAlign: 'center', fontSize: '0.72rem' };
const inp = { border: '1px solid #ced4da', borderRadius: 3, padding: '3px 6px', fontSize: '0.8rem', width: '100%', boxSizing: 'border-box' };
// Card chrome for the single selected sample in 'tabs' mode — title-bar
// header (name + delete) separated from the field list by a border.
const cardStyle = { background: '#fff', border: '1px solid #dee2e6', borderRadius: 10, overflow: 'hidden', marginBottom: 16 };
const cardHeadStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#f8f9fa', borderBottom: '1px solid #dee2e6' };
const cardTitleStyle = { fontWeight: 700, fontSize: '0.9rem' };
const cardBodyStyle = { padding: '4px 14px 8px' };
const cardFieldLabelStyle = { fontSize: '0.72rem', color: '#6c757d', flexShrink: 0, width: 140, whiteSpace: 'pre-line' };
const cardFieldRowStyle = (first) => ({ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderTop: first ? 'none' : '1px solid #f1f3f5' });
const cardGroupHeaderStyle = { ...CG, borderRadius: 6, padding: '6px 8px', margin: '8px 0' };
// "+ Näyte" — green text-action, matching "+ Lisää rakenneosa"/"+ Uusi osio"
// elsewhere in this redesign, instead of a standalone yellow pill.
const addSampleBtnStyle = { display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', background: 'none', color: '#198754', fontWeight: 600, fontSize: '0.86rem', cursor: 'pointer', padding: '4px 2px' };
const addSampleDotStyle = { width: 18, height: 18, borderRadius: '50%', border: '1.5px dashed #198754', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', flexShrink: 0, color: '#198754' };

// DECIMAL(6,1) columns come back from the API as fixed-precision strings
// ("11.0") — strip the trailing zero for display; typing still accepts
// decimals ("11.5"), only the loaded/committed value gets reformatted.
const fmtNum = (v) => {
  if (v === null || v === undefined || v === '') return '';
  const n = parseFloat(v);
  return Number.isNaN(n) ? v : String(n);
};

const ROWS = [
  { key: 'nayte_tunnus', label: 'Näytetunnus' },
  { key: 'testi', label: 'Testi', isTesti: true },
  { key: 'testaussyvyys', label: 'Testaussyvyys\npinta/mm' },
  { key: 'naytteen_pinta', label: 'Näytteen\nØ mm' },
  { key: 'terakseen_sijainti', label: 'Teräksen sijainti\nmm (Ø mm)' },
  { key: '__karbhdr', groupHeader: 'Karbonatisoitumissyvyys mm' },
  // Which physical surface each slot actually is — defaults from the
  // rakenneosa's tyyppi on sample creation (e.g. Parvekelaatta -> yläpinta/
  // alapinta), overridable per sample here, same as v1's KARB_PINTA_OPTIONS.
  { key: 'karb_upper_pinta', label: 'PINTA 1 — pinta', isSelect: ['yläpinta', 'ulkopinta', 'rp0'] },
  { key: 'karb_yp_min', label: 'PINTA 1 — min', numeric: true }, { key: 'karb_yp_max', label: 'PINTA 1 — max', numeric: true }, { key: 'karb_yp_km', label: 'PINTA 1 — k-m', numeric: true },
  { key: 'karb_lower_pinta', label: 'PINTA 2 — pinta', isSelect: ['alapinta', 'sisäpinta', 'rp1'] },
  { key: 'karb_ap_min', label: 'PINTA 2 — min', numeric: true }, { key: 'karb_ap_max', label: 'PINTA 2 — max', numeric: true }, { key: 'karb_ap_km', label: 'PINTA 2 — k-m', numeric: true },
  { key: 'karb_to_table', label: '→ taulukkoon', isToggle: [['upper', 'Ylempi'], ['lower', 'Alempi']] },
  { key: 'lisahuomioita', label: 'Lisähuomioita', expanding: true },
  { key: 'rakennepaksuus', label: 'Rakennepaksuus\nmm' },
];

export default function PoranayteView({ samples, testiTyypit, loading, patch, toggleTesti, removeSample, caption, commitCaption, rakenneosaId, taulukkoNum = 1, onAddSample }) {
  // Lisähuomioita fires onChange on every keystroke (Lexical) — debounce the
  // actual save so typing doesn't send a request (and a success banner) per
  // character, same 600ms pattern v1's PoranayteBlock uses for karb fields.
  const noteTimers = useRef({});
  const patchNoteDebounced = (sampleId, value) => {
    clearTimeout(noteTimers.current[sampleId]);
    noteTimers.current[sampleId] = setTimeout(() => patch(sampleId, 'lisahuomioita', value), 600);
  };

  const isMobile = useIsMobile();

  // Two view modes:
  //  - 'table': full side-by-side table, one column per sample (desktop default).
  //  - 'tabs': genuinely ONE sample at a time — a dropdown on mobile, a tab
  //    strip on desktop (see the isMobile branch below) — what "Näytä yksi
  //    kerrallaan" actually promises. Mobile default (the side-by-side
  //    table is unusable below 768px); desktop starts on 'table' and
  //    reaches 'tabs' via "Näytä yksi kerrallaan".
  // There used to be a third 'cards' mode (every sample stacked at once) —
  // removed; 'tabs' with the mobile dropdown covers the same "small screen,
  // one sample's fields visible at a time" need without a separate mode.
  // Initial pick is read once at mount, not on every resize, so a manual
  // switch isn't fought by a later resize — this is a view preference, not
  // data, so it isn't persisted.
  const [viewMode, setViewMode] = useState(() => (window.innerWidth >= 768 ? 'table' : 'tabs'));
  const [selectedSampleId, setSelectedSampleId] = useState(null);
  const selectedSample = samples.find(s => s.id === selectedSampleId) ?? samples[0];

  // onAddSample resolves to the created sample (usePoranayteV2.addSample)
  // — select it immediately so a newly added näyte is the one you land on
  // in the tabs view, instead of staying on whichever was selected before
  // (or defaulting to the first one).
  const handleAddSample = async () => {
    const created = await onAddSample();
    if (created) setSelectedSampleId(created.id);
  };

  // Shared by both the full table and the stacked cards below so the
  // ~45 lines of field-type branching only exist once.
  const renderFieldCell = (row, s) => (
    row.isTesti ? (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {testiTyypit.map(opt => {
          const checked = (s.testi || '').split(',').map(x => x.trim()).includes(opt);
          return (
            <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={checked} onChange={e => toggleTesti(s, opt, e.target.checked)} style={{ margin: 0, accentColor: '#04aa00' }} />
              {opt}
            </label>
          );
        })}
      </div>
    ) : row.isSelect ? (
      <select style={inp} value={s[row.key] || ''} onChange={e => patch(s.id, row.key, e.target.value)}>
        <option value="">—</option>
        {row.isSelect.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    ) : row.isToggle ? (
      <div style={{ display: 'inline-flex', border: '1px solid #ced4da', borderRadius: 20, overflow: 'hidden' }}>
        {row.isToggle.map(([val, label]) => {
          const active = (s[row.key] || '') === val;
          return (
            <button key={val} type="button" onClick={() => patch(s.id, row.key, val)}
              style={{ border: 'none', padding: '3px 10px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                background: active ? '#04aa00' : '#fff', color: active ? '#fff' : '#6c757d',
                transition: 'background 0.15s ease, color 0.15s ease' }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#f1f3f5'; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.background = '#fff'; }}>
              {label}
            </button>
          );
        })}
      </div>
    ) : row.expanding ? (
      <PlainTextExpandingInput
        namespace={`poranayte-${row.key}-${s.id}`}
        value={s[row.key] ?? ''}
        onChange={val => patchNoteDebounced(s.id, val)}
        placeholder="Lisähuomioita…"
      />
    ) : (
      <input style={inp} defaultValue={row.numeric ? fmtNum(s[row.key]) : (s[row.key] ?? '')}
        onBlur={e => { if (e.target.value !== (s[row.key] ?? '')) patch(s.id, row.key, e.target.value); }} />
    )
  );

  return (
    <div style={{ overflowX: 'auto', position: 'relative', minHeight: loading ? 80 : undefined }}>
      <TableLoadingOverlay show={loading} />
      {!loading && samples.length === 0 ? (
        // A freshly-created rakenneosa has no samples yet — showing the
        // table here rendered just the label column (every row's field
        // cells are samples.map, so 0 samples = 0 data columns), which
        // looked like a broken/empty table rather than an empty state.
        // Show the add-sample action centered in its place instead.
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '32px 16px', color: '#6c757d', fontSize: '0.85rem' }}>
          <div>Ei vielä näytteitä tällä rakenneosalla.</div>
          <button type="button" onClick={handleAddSample} style={addSampleBtnStyle}>
            <span style={addSampleDotStyle}>+</span>Näyte
          </button>
        </div>
      ) : viewMode === 'table' ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
            <button type="button" onClick={() => setViewMode('tabs')}
              style={{ border: 'none', background: 'none', color: '#0d6efd', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
              ⇆ Näytä yksi kerrallaan
            </button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
            <button type="button" onClick={handleAddSample} style={addSampleBtnStyle}>
              <span style={addSampleDotStyle}>+</span>Näyte
            </button>
          </div>
          <table style={{ borderCollapse: 'collapse', background: '#fff', tableLayout: 'fixed', width: '100%' }}>
            <tbody>
              {ROWS.map(row => row.groupHeader ? (
                <tr key={row.key}><td style={CG} colSpan={samples.length + 1}>{row.groupHeader}</td></tr>
              ) : (
                <tr key={row.key}>
                  <td style={CL}>{row.label}</td>
                  {samples.map(s => (
                    <td key={s.id} style={C}>{renderFieldCell(row, s)}</td>
                  ))}
                </tr>
              ))}
              <tr>
                <td style={{ ...CL, background: '#fff' }} />
                {samples.map(s => (
                  <td key={s.id} style={{ ...C, textAlign: 'center' }}>
                    <button type="button" onClick={() => removeSample(s.id)}
                      style={{ border: 'none', background: 'none', color: '#adb5bd', cursor: 'pointer', fontSize: '1rem', transition: 'color 0.15s ease' }}
                      onMouseEnter={e => { e.currentTarget.style.color = '#dc3545'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = '#adb5bd'; }}>×</button>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </>
      ) : (
        // Genuinely one sample at a time — a dropdown (mobile) or tab
        // strip (desktop) switches which sample's card is shown below.
        <div>
          {/* On mobile "+ Näyte" moves up here, always visible — inside the
              scrollable tab strip it ended up past the last tab, off-screen
              until you scrolled all the way right (easy to lose entirely
              with more than a handful of samples). Desktop keeps it right
              after the last tab, where there's room for it to just show. */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginBottom: 6 }}>
            {isMobile && (
              <button type="button" onClick={handleAddSample} style={addSampleBtnStyle}>
                <span style={addSampleDotStyle}>+</span>Näyte
              </button>
            )}
            <button type="button" onClick={() => setViewMode('table')}
              style={{ marginLeft: isMobile ? 0 : 'auto', border: 'none', background: 'none', color: '#0d6efd', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
              ⇆ Kaikki rinnakkain
            </button>
          </div>

          {isMobile ? (
            // A horizontal tab strip doesn't work on a phone — with more
            // than a handful of samples it needs scrolling with no sense
            // of how many there are or which one you're looking for. A
            // plain dropdown scales to any sample count the same way, no
            // scrolling, nothing to lose off-screen. Delete lives on the
            // card below only here (no per-option delete in a <select>).
            // Native select-arrow rendering varies by browser/OS and looked
            // out of place next to everything else here — appearance:none
            // hides it, a plain chevron character replaces it instead.
            <div style={{ position: 'relative', marginBottom: 14 }}>
              <select value={selectedSample?.id ?? ''} onChange={e => setSelectedSampleId(Number(e.target.value))}
                style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none', width: '100%', border: '1px solid #ced4da', borderRadius: 8, padding: '10px 36px 10px 12px', fontSize: '0.92rem', fontWeight: 600, color: '#1c1c1e', background: '#fff' }}>
                {samples.map((s, i) => (
                  <option key={s.id} value={s.id}>{s.nayte_tunnus || `Näyte ${i + 1}`}</option>
                ))}
              </select>
              <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#6c757d', fontSize: '0.8rem' }}>▾</span>
            </div>
          ) : (
            // Desktop keeps the horizontal tab strip — plenty of width,
            // no overflow problem there. Each tab gets its own delete "×"
            // so deleting doesn't require selecting a tab first.
            <>
              <div className="poranayte-tab-scroll" style={{ display: 'flex', alignItems: 'center', gap: 2, overflowX: 'auto', borderBottom: '1px solid #e9ecef', marginBottom: 14 }}>
                {samples.map((s, i) => {
                  const active = selectedSample?.id === s.id;
                  return (
                    <span key={s.id} style={{ display: 'inline-flex', alignItems: 'center', borderBottom: '2px solid ' + (active ? '#198754' : 'transparent'), flexShrink: 0 }}>
                      <button type="button" onClick={() => setSelectedSampleId(s.id)}
                        style={{ border: 'none', background: 'none', padding: '8px 4px 8px 14px', fontSize: '0.82rem', fontWeight: active ? 700 : 500, color: active ? '#198754' : '#6c757d', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                        {s.nayte_tunnus || `Näyte ${i + 1}`}
                      </button>
                      <button type="button" onClick={() => removeSample(s.id)} title="Poista näyte"
                        style={{ border: 'none', background: 'none', color: '#adb5bd', cursor: 'pointer', fontSize: '0.95rem', padding: '0 10px 0 2px', lineHeight: 1, transition: 'color 0.15s ease' }}
                        onMouseEnter={e => { e.currentTarget.style.color = '#dc3545'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = '#adb5bd'; }}>×</button>
                    </span>
                  );
                })}
                <button type="button" onClick={handleAddSample} style={{ ...addSampleBtnStyle, flexShrink: 0, padding: '8px 10px 8px 2px' }}>
                  <span style={addSampleDotStyle}>+</span>Näyte
                </button>
              </div>
              <style>{`.poranayte-tab-scroll { scrollbar-width: none; } .poranayte-tab-scroll::-webkit-scrollbar { display: none; }`}</style>
            </>
          )}

          {selectedSample && (
            // key forces a full remount on sample switch — renderFieldCell's
            // plain <input>s are uncontrolled (defaultValue only applies on
            // initial mount), so without this React reuses the same DOM
            // nodes across tabs and silently keeps showing whichever
            // sample's values they first mounted with (same fix as the
            // single-sample tabs this view had before — see 10/1's "Poränäyte
            // (v2): mobile single-sample view" commit).
            <div key={selectedSample.id} style={cardStyle}>
              <div style={cardHeadStyle}>
                <div style={cardTitleStyle}>{selectedSample.nayte_tunnus || `Näyte ${samples.indexOf(selectedSample) + 1}`}</div>
                <button type="button" onClick={() => removeSample(selectedSample.id)}
                  style={{ border: 'none', background: 'none', color: '#adb5bd', cursor: 'pointer', fontSize: '1rem', padding: 0, transition: 'color 0.15s ease' }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#dc3545'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#adb5bd'; }}>×</button>
              </div>
              <div style={cardBodyStyle}>
                {ROWS.map((row, ri) => row.groupHeader ? (
                  <div key={row.key} style={cardGroupHeaderStyle}>{row.groupHeader}</div>
                ) : (
                  <div key={row.key} style={cardFieldRowStyle(ri === 0)}>
                    <div style={cardFieldLabelStyle}>{row.label}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>{renderFieldCell(row, selectedSample)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {/* kohdeId is keyed off rakenneosaId — the already-fetched rakenneosa
          row, not the rakId prop (the selector) — see PeitepaksuusView for
          why: keying off the selector remounts the editor before its
          caption fetch resolves, locking it onto stale text. */}
      <TaulukkoFooter taulukkoNum={taulukkoNum} kohdeId={rakenneosaId ?? 'preview'} caption={caption} onChange={commitCaption} />
    </div>
  );
}
